import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatRupiah, formatTanggal } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

export default function VerifyPage() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/verify/${code}`);
        setState({ loading: false, data, error: null });
      } catch (e) {
        setState({ loading: false, data: null, error: e?.response?.data?.message || "Surat tidak ditemukan." });
      }
    })();
  }, [code]);

  if (state.loading) return <div className="flex h-screen items-center justify-center text-sm text-ink-500">Memverifikasi…</div>;

  const ok = state.data?.valid;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 hpm-grid-bg flex items-center justify-center p-6" data-testid="verify-page">
      <Card className={`w-full max-w-xl overflow-hidden border shadow-sm ${ok ? "border-emerald-300" : "border-rose-300"}`}>
        <div className={`px-6 py-8 text-white ${ok ? "bg-gradient-to-br from-emerald-500 to-emerald-700" : "bg-gradient-to-br from-rose-500 to-rose-700"}`}>
          <div className="flex items-center gap-3">
            <div className="grid h-14 w-14 place-items-center rounded-full bg-white/15 ring-2 ring-white/30">
              {ok ? <ShieldCheck className="h-7 w-7" /> : <XCircle className="h-7 w-7" />}
            </div>
            <div>
              <p className="overline text-white/70">Verifikasi Dokumen</p>
              <h1 className="font-heading text-3xl font-bold tracking-tight">{ok ? "Surat Sah" : "Surat Tidak Valid"}</h1>
            </div>
          </div>
          {ok && (<p className="mt-4 text-sm text-white/90">Dokumen di bawah ini benar diterbitkan oleh <strong>{state.data.diterbitkan_oleh}</strong>.</p>)}
        </div>

        <div className="p-6">
          {ok ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="col-span-1 text-ink-500">Nomor</div><div className="col-span-2 font-mono font-medium">{state.data.nomor}</div>
                <div className="col-span-1 text-ink-500">Jenis</div><div className="col-span-2"><Badge className={state.data.jenis==='SPH'?"bg-sky-100 text-sky-800 hover:bg-sky-100":"bg-amber-100 text-amber-800 hover:bg-amber-100"}>{state.data.jenis}</Badge></div>
                <div className="col-span-1 text-ink-500">Tanggal</div><div className="col-span-2">{formatTanggal(state.data.tanggal)}</div>
                <div className="col-span-1 text-ink-500">Pemohon</div><div className="col-span-2 font-medium">{state.data.nama_pemohon}</div>
                {state.data.kategori && (<><div className="col-span-1 text-ink-500">Kategori</div><div className="col-span-2">{state.data.kategori}</div></>)}
                <div className="col-span-1 text-ink-500">Status</div><div className="col-span-2"><Badge>{state.data.status}</Badge></div>
              </div>
              {state.data.products?.length > 0 && (
                <div className="rounded-md border border-border bg-secondary/30 p-3">
                  <p className="overline mb-2">Produk</p>
                  <ul className="space-y-1 text-sm">
                    {state.data.products.map((p,i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{p.nama} <span className="text-ink-500">× {p.qty}</span></span>
                        <span className="font-mono">{formatRupiah(p.harga*p.qty)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="h-4 w-4" /> <span className="text-sm font-medium">Total nilai surat</span></div>
                <div className="font-mono text-lg font-bold text-emerald-900">{formatRupiah(state.data.total)}</div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-ink-500">{state.error || "QR ini tidak terhubung dengan surat manapun di sistem. Jika Anda yakin ini surat resmi, hubungi penerbit untuk konfirmasi."}</div>
          )}
        </div>
      </Card>
    </div>
  );
}
